/**
 * `futurex build "<task>"` / REPL `/build` — execute AGAINST the pre-build plan
 * (plan-command.ts) instead of improvising: each plan task becomes one agent
 * turn (with the plan's goal + decided tooling injected), progress is evaluated
 * against the acceptance criteria after every task — the build STOPS when all
 * are met — and the token budget (effort × value) is enforced throughout.
 * Executed tasks are checkpointed, so a killed build resumes where it stopped.
 */
import { createInterface } from 'node:readline';
import { createAiClient } from '@fim/ai-sdk';
import { evaluateGoal, type GoalEvaluation } from './goal.js';
import { loadContext } from './context.js';
import { createPermissionGate } from './permissions.js';
import { createReadlineAsk } from './agent.js';
import { createReplRuntime } from './repl-runtime.js';
import { runDiagnostics } from './diagnostics.js';
import { ensureBuildPlan, printBuildPlan, type PlanFlags } from './plan-command.js';
import { scheduleByDeps, type BuildPlan, type Task } from './planner.js';
import { readCheckpoint, writeCheckpoint } from './pipeline.js';
import { addUsage, emptyTotals, formatUsage } from './pricing.js';
import { findRepoRoot } from './repo.js';
import type { CliOptions } from './options.js';
import { color, error, info, line } from './ui.js';

/** Why an execution ended. */
export type BuildStatus = 'done' | 'tasks-exhausted' | 'budget-exhausted' | 'aborted';

export interface BuildResult {
  readonly status: BuildStatus;
  readonly completedTasks: readonly string[];
  readonly evaluation?: GoalEvaluation;
}

/** Everything executePlan needs, injected so the loop is unit-testable. */
export interface ExecutePlanDeps {
  readonly plan: BuildPlan;
  /** Run one agent turn for a task prompt. */
  turn(prompt: string): Promise<void>;
  /** Judge accumulated evidence against the goal's criteria. */
  evaluate(evidence: string): Promise<GoalEvaluation>;
  /** Optional validation pass (typecheck/lint/test); lines feed the evidence. */
  verify?(): readonly { name: string; ok: boolean }[];
  /** Output tokens spent so far (the budget is an output-token allowance). */
  usedTokens(): number;
  /** Has this task already been executed (resume)? */
  isTaskDone(taskId: string): boolean;
  markTaskDone(taskId: string): void;
  report(text: string): void;
  readonly signal?: AbortSignal;
}

/** The plan context injected into every task turn, so tooling isn't improvised. */
export function buildTaskPrompt(plan: BuildPlan, task: Task, isFirst: boolean): string {
  const parts: string[] = [
    `You are executing ONE step of an approved build plan. Work only toward this step.`,
    `END GOAL: ${plan.goal.statement}`,
  ];
  if (plan.tooling.length > 0) {
    parts.push(
      'TOOLING (decided in the plan — use these, do not substitute alternatives):',
      ...plan.tooling.map((t) => `- ${t.area}: ${t.chosen}`),
    );
  }
  if (isFirst && plan.research.length > 0) {
    parts.push(
      'Before implementing, ground yourself on these questions (use read_file/grep_search/list_dir):',
      ...plan.research.map((r) => `- ${r.question}`),
    );
  }
  parts.push(`STEP ${task.id}: ${task.title}`, task.description);
  if (task.targets.length > 0) parts.push(`Expected to touch: ${task.targets.join(', ')}`);
  return parts.join('\n');
}

/**
 * Execute the plan's task DAG in dependency order: budget check → agent turn →
 * checkpoint → (scheduled) validation pass → goal evaluation; stop on done.
 */
export async function executePlan(deps: ExecutePlanDeps): Promise<BuildResult> {
  const { plan } = deps;
  const ordered = scheduleByDeps(plan.tasks).flat();
  const completed: string[] = [];
  const evidence: string[] = [
    `Goal: ${plan.goal.statement}`,
    `Planned tasks: ${plan.tasks.map((t) => `${t.id} (${t.title})`).join(', ')}`,
  ];
  let evaluation: GoalEvaluation | undefined;

  // Spread the budgeted validation passes evenly across the task list; the
  // last task always qualifies, so even a 1-task plan gets its pass.
  const passes = plan.budget.validationPasses;
  const interval = passes > 0 ? Math.max(1, Math.floor(ordered.length / (passes + 1))) : Number.POSITIVE_INFINITY;
  let passesRun = 0;
  let researchInjected = false;

  for (let i = 0; i < ordered.length; i++) {
    const task = ordered[i];
    if (!task) continue;
    if (deps.signal?.aborted) return { status: 'aborted', completedTasks: completed, ...(evaluation ? { evaluation } : {}) };

    if (deps.isTaskDone(task.id)) {
      completed.push(task.id);
      evidence.push(`Task ${task.id} "${task.title}": completed in a previous run.`);
      deps.report(`✓ ${task.id} ${task.title} (resumed)`);
      continue;
    }

    const used = deps.usedTokens();
    if (used >= plan.budget.tokens) {
      deps.report(
        `budget exhausted: ${used.toLocaleString()} / ${plan.budget.tokens.toLocaleString()} output tokens — ` +
          'stopping (re-run with --budget <n> or a higher --value to continue)',
      );
      return { status: 'budget-exhausted', completedTasks: completed, ...(evaluation ? { evaluation } : {}) };
    }

    deps.report(`▶ ${task.id} ${task.title}`);
    // Research grounds the FIRST task actually executed this run — also after a
    // resume, where the new process has none of the prior grounding in context.
    await deps.turn(buildTaskPrompt(plan, task, !researchInjected));
    researchInjected = true;
    if (deps.signal?.aborted) return { status: 'aborted', completedTasks: completed, ...(evaluation ? { evaluation } : {}) };
    deps.markTaskDone(task.id);
    completed.push(task.id);
    evidence.push(`Task ${task.id} "${task.title}": executed (targets: ${task.targets.join(', ') || 'n/a'}).`);

    if (deps.verify && passesRun < passes && ((i + 1) % interval === 0 || i === ordered.length - 1)) {
      passesRun += 1;
      const results = deps.verify();
      const summary = results.map((r) => `${r.name}=${r.ok ? 'pass' : 'FAIL'}`).join(' ');
      evidence.push(`Validation pass ${passesRun}: ${summary || 'no checks detected'}`);
      deps.report(`verify: ${summary || 'no checks detected'}`);
    }

    evaluation = await deps.evaluate(evidence.join('\n'));
    deps.report(
      `progress: ${evaluation.met.length}/${plan.goal.criteria.length} criteria met` +
        (evaluation.remaining.length > 0 ? ` — remaining: ${evaluation.remaining.join(', ')}` : ''),
    );
    if (evaluation.done) {
      deps.report('all acceptance criteria met — stopping here instead of continuing open-endedly');
      return { status: 'done', completedTasks: completed, evaluation };
    }
  }

  return { status: 'tasks-exhausted', completedTasks: completed, ...(evaluation ? { evaluation } : {}) };
}

/** `futurex build "<task>"` — plan (or resume the plan), then execute against it. */
export async function runBuild(task: string, opts: CliOptions, flags: PlanFlags = {}): Promise<void> {
  const q = task.trim();
  if (!q) {
    error('usage: futurex build "<task>"');
    process.exitCode = 1;
    return;
  }

  const outcome = await ensureBuildPlan(q, opts, flags, (name, status) =>
    info(`  ${status === 'cached' ? '✓ resumed' : '… running'} ${name}`),
  );
  if (outcome.kind === 'clarify') {
    info('one question before building:');
    line(`  ? ${outcome.question}`);
    line(color.dim('(answer by re-running build with the detail added)'));
    return;
  }
  const { id, plan } = outcome;
  printBuildPlan(plan);
  line();

  const ctx = loadContext(opts);
  const root = findRepoRoot();
  const rl = createInterface({ input: process.stdin, output: process.stdout });
  const gate = createPermissionGate(ctx.root, createReadlineAsk(rl));
  const ac = new AbortController();
  const onSigint = (): void => ac.abort();
  // With a readline attached to the TTY, Ctrl+C is delivered to the INTERFACE,
  // not the process — listen on both so the first Ctrl+C aborts cleanly.
  rl.on('SIGINT', onSigint);
  process.on('SIGINT', onSigint);
  const totals = emptyTotals();
  const ai = createAiClient({ provider: 'deepseek' });

  try {
    const runtime = createReplRuntime(ctx, opts, { autoApprove: false, maxSteps: 12 }, gate);
    const result = await executePlan({
      plan,
      signal: ac.signal,
      async turn(prompt) {
        const usage = await runtime.turn(prompt, opts.model, ac.signal);
        if (usage) addUsage(totals, opts.model, usage);
      },
      evaluate: (evidence) => evaluateGoal(plan.goal, evidence, ai, { model: opts.model }),
      verify: () => runDiagnostics(root).map((r) => ({ name: r.name, ok: r.ok })),
      usedTokens: () => totals.outputTokens + totals.reasoningTokens,
      isTaskDone: (taskId) => readCheckpoint(id, `exec-${taskId}`) !== null,
      markTaskDone: (taskId) => writeCheckpoint(id, `exec-${taskId}`, { completed: true }),
      report: (text) => info(text),
    });

    line();
    info(`build ${result.status} · ${result.completedTasks.length}/${plan.tasks.length} tasks · ${formatUsage(totals).join(' · ')}`);
    if (result.evaluation && !result.evaluation.done) {
      info(`remaining criteria: ${result.evaluation.remaining.join(', ')}`);
    }
  } catch (err) {
    if (!ac.signal.aborted) {
      error(err instanceof Error ? err.message : String(err));
      process.exitCode = 1;
    } else {
      info('cancelled');
    }
  } finally {
    rl.close();
    process.removeListener('SIGINT', onSigint);
  }
}
